import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Button } from '@pesto/ui/components/Button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@pesto/ui/components/Collapsible';
import { Label } from '@pesto/ui/components/Label';
import { PhoneInput } from '@pesto/ui/components/PhoneInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pesto/ui/components/Select';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useController, type UseFormReturn } from 'react-hook-form';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import type { ModalProps } from '../../types/modal.types';

import {
  AdminRole,
  type CreateAdminUserInput,
  useCreateAdminUserMutationMutation,
  useListPartnersQuery,
  useListProgramsQuery,
} from '../../../__generated__/graphql/api';
import { getUsersListMutationRefreshOptions } from '../../../utils/userCacheInvalidation';

const ALL_PROGRAMS = 'all-programs';

const stringSchema = (field: string) => z.string().max(255, `${field} is too long`);

const createAdminUserSchema = z
  .object({
    email: stringSchema('Email').email('Invalid email format').min(1, 'Email is required'),
    first_name: stringSchema('First name').min(1, 'First name is required'),
    last_name: stringSchema('Last name').min(1, 'Last name is required'),
    phone: stringSchema('Phone').refine((val) => isPossiblePhoneNumber(val), {
      message: 'Invalid phone number',
    }),
    role: z.nativeEnum(AdminRole, { errorMap: () => ({ message: 'Role is required' }) }),
    partner_id: z.string().optional(),
    program_scope: z.string().optional(),
    auth_id: z.string().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (data.role === AdminRole.PartnerAdmin && !data.partner_id) {
      ctx.addIssue({ code: 'custom', path: ['partner_id'], message: 'Partner is required for partner admins' });
    }

    if (data.role === AdminRole.PartnerAdmin && !data.program_scope) {
      ctx.addIssue({ code: 'custom', path: ['program_scope'], message: 'Choose all programs or one program' });
    }
  });

type CreateAdminFormData = z.infer<typeof createAdminUserSchema>;

const ROLE_OPTIONS = [
  { label: 'Partner Admin', value: AdminRole.PartnerAdmin },
  { label: 'Support Agent', value: AdminRole.SupportAgent },
];

const baseFormConfig: SmartFormConfig<CreateAdminFormData> = {
  fields: [
    { name: 'email', label: 'Email', type: 'text', defaultValue: '', required: true },
    { name: 'first_name', label: 'First Name', type: 'text', defaultValue: '', required: true },
    { name: 'last_name', label: 'Last Name', type: 'text', defaultValue: '', required: true },
    { name: 'phone', label: 'Phone', component: PhoneInput, defaultValue: '', required: true },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      options: ROLE_OPTIONS,
      defaultValue: AdminRole.PartnerAdmin,
      required: true,
    },
    {
      name: 'partner_id',
      label: 'Partner',
      type: 'select',
      options: [],
      defaultValue: '',
      placeholder: 'Select partner',
      conditional: { when: { field: 'role', operator: 'equals', value: AdminRole.PartnerAdmin } },
    },
  ],
  schema: createAdminUserSchema,
  defaultValues: {
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: AdminRole.PartnerAdmin,
    partner_id: '',
    program_scope: '',
    auth_id: '',
  },
};

type ScopedProgram = { id: string; name: string; active: boolean; partner: { id: string } };

const ProgramScopeSelect = ({
  form,
  programs,
}: {
  form: UseFormReturn<CreateAdminFormData>;
  programs: ScopedProgram[];
}) => {
  const partnerId = form.watch('partner_id');
  const { field, fieldState } = useController({ control: form.control, name: 'program_scope' });
  const { resetField } = form;

  useEffect(() => {
    resetField('program_scope');
  }, [partnerId, resetField]);

  if (form.watch('role') !== AdminRole.PartnerAdmin || !partnerId) return null;

  const partnerPrograms = programs.filter((program) => program.partner.id === partnerId && program.active);
  const error = fieldState.error?.message;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="create-admin-program-scope">Programs</Label>
      <Select value={field.value ?? ''} onValueChange={field.onChange}>
        <SelectTrigger id="create-admin-program-scope">
          <SelectValue placeholder="Select scope" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_PROGRAMS}>All programs</SelectItem>
          {partnerPrograms.map((program) => (
            <SelectItem key={program.id} value={program.id}>
              {program.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <span className="text-destructive text-sm">{error}</span>}
    </div>
  );
};

const partnerScope = (data: CreateAdminFormData) => {
  if (data.role !== AdminRole.PartnerAdmin) return {};

  return data.program_scope === ALL_PROGRAMS ? { partnerId: data.partner_id } : { adminProgramId: data.program_scope };
};

export const CreateAdminModalComponent = ({ api }: ModalProps<undefined, { userId: string | null | undefined }>) => {
  const { handleMutation } = useMutationWithToast();
  const [createAdminUser, { loading }] = useCreateAdminUserMutationMutation(getUsersListMutationRefreshOptions());

  const { data: partnersData, loading: partnersLoading } = useListPartnersQuery();

  const { data: programsData } = useListProgramsQuery({
    variables: { fullResults: true, paginationDetailsOnly: false },
  });

  const partnerOptions = (partnersData?.listPartners ?? []).flatMap((partner) =>
    partner?.active ? [{ value: partner.id, label: partner.name }] : [],
  );

  const configWithPartners: SmartFormConfig<CreateAdminFormData> = {
    ...baseFormConfig,
    fields: baseFormConfig.fields?.map((field) =>
      field.name === 'partner_id' && field.type === 'select' ? { ...field, options: partnerOptions } : field,
    ),
  };

  const handleSubmit = async (data: CreateAdminFormData) => {
    const input: CreateAdminUserInput = {
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      role: data.role,
      ...partnerScope(data),
    };

    if (data.auth_id) input.authId = data.auth_id;

    await handleMutation({
      mutation: () => createAdminUser({ variables: { input } }),
      extractSuccess: (result) => !!result?.data?.createAdminUser?.id,
      onSuccess: (result) => {
        const userId = result?.data?.createAdminUser?.id;
        api.close({ userId });
      },
      debugLabel: 'create-admin-modal',
      toast: {
        success: 'Admin created successfully',
        error: 'Failed to create admin',
        loading: 'Creating admin...',
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <SmartForm
        config={configWithPartners}
        onSubmit={handleSubmit}
        loading={loading || partnersLoading}
        bottomContent={(form) => (
          <div className="mt-6 flex flex-col gap-3">
            <ProgramScopeSelect form={form} programs={programsData?.listPrograms?.programs ?? []} />
            <Collapsible className="text-muted-foreground text-sm">
              <CollapsibleTrigger className="flex items-center gap-1 hover:text-foreground">
                <ChevronDownIcon className="h-3.5 w-3.5" />
                Advanced
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <label className="flex flex-col gap-1">
                  <span className="text-foreground">Auth0 ID (optional)</span>
                  <input
                    type="text"
                    className="border rounded-md px-3 py-2 text-foreground bg-background"
                    placeholder="auth0|abc123"
                    value={form.watch('auth_id') ?? ''}
                    onChange={(e) => form.setValue('auth_id', e.target.value)}
                  />
                  <span className="text-xs text-muted-foreground">
                    Provide an existing Auth0 user ID to link instead of creating a new one.
                  </span>
                </label>
              </CollapsibleContent>
            </Collapsible>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => api.cancel()}>
                Cancel
              </Button>
              <Button onClick={(event) => void form.handleSubmit(handleSubmit)(event)} variant="primary" size="sm">
                Create Admin
              </Button>
            </div>
          </div>
        )}
      />
    </div>
  );
};
